import { Component } from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SettingsService } from '@core';
import { AuthService } from '@core/services/auth/auth.service';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { SftpInfo, SftpInfoState } from '@core/services/sftp/sftp-info.model';
import { SftpInfoService } from '@core/services/sftp/sftp-info.service';
import { process } from '@progress/kendo-data-query';
import { select, Store } from '@ngrx/store';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { GridSettings } from '@shared/components/customized-grid/grid-settings/gird-settings.interface';
import { inputNoWhitespaceValidator } from '@shared/directives/input-no-whitespace.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { SftpEditComponent } from './sftp-edit/sftp-edit.component';
import * as sftpActions from './state/sftp-info.actions';
import * as fromSftp from './state/sftp-info.reducer';

@Component({
  selector: 'Teranet-Sftp',
  templateUrl: './sftp.component.html',
})
export class SftpComponent extends BaseComponent {
  deletedstaus: any;
  error$: Observable<String>;
  gridSettings: GridSettings;
  hide = true;
  temp = [];
  services: any;
  sftpForm: FormGroup;
  sftps: SftpInfo[] = [];
  sftps$: Observable<SftpInfo[]>;

  constructor(
    private spinner: NgxSpinnerService,
    private sftpService: SftpInfoService,    
    private localStorageService: LocalStorageService,
    private auth: AuthService,
    public dialog: MatDialog,
    private store: Store<SftpInfoState>,
    private settingsService: SettingsService
  ) {
    super(settingsService);
    this.sftpForm = new FormGroup({
      id: new FormControl(0),
      hostname: new FormControl('', [inputNoWhitespaceValidator, Validators.required]),
      sftpname: new FormControl('', [inputNoWhitespaceValidator, Validators.required]),
      port: new FormControl('', [Validators.required, Validators.pattern(/^-?(0|[1-9]\d*)?$/)]),
      username: new FormControl('', [inputNoWhitespaceValidator, Validators.minLength(5), Validators.required]),
      password: new FormControl('', [inputNoWhitespaceValidator, Validators.minLength(4), Validators.required]),
      isexternal: new FormControl(false),
      isenabled: new FormControl(true),
    });

    this.sftps$ = new Observable<SftpInfo[]>();
    this.sftpService.isDataChanged$.subscribe(data => {
      if (data) {
        this.refesh();
      }
    });
  }

  public state: any = {
    skip: 0,
    take: 13,
    filter: {
      logic: 'and',
      filters: [],
    },
  };

  ngOnInit() {
    this.gridSettings = this.GridDefaultSettings();
    this.sftpService.APIEndpoint = this.localStorageService.getItem('apiEndpoint');
    this.spinner.show();
    setTimeout(() => {
      this.refesh();
      this.spinner.hide();
    }, 2000);
  }

  refesh() {
    this.store.dispatch(new sftpActions.LOADSFTPINFOS());
    this.sftps$ = this.store.pipe(select(fromSftp.getSftpInfos));
    this.error$ = this.store.pipe(select(fromSftp.getError));

    this.subscription = this.sftps$.subscribe(sftps => {
      this.sftps = sftps;
      this.gridSettings.gridData = { data: this.sftps, total: this.sftps.length };
      this.temp = this.sftps;
    });
  }

  createSftp(form: NgForm) {
    if (this.sftpForm.valid) {
      const newsftp: SftpInfo = {
        id: 0,
        hostname: this.sftpForm.get('hostname').value,
        sftpname: this.sftpForm.get('sftpname').value,
        port: Number(this.sftpForm.get('port').value),
        username: this.sftpForm.get('username').value,
        password: this.sftpForm.get('password').value,
        isexternal: this.sftpForm.get('isexternal').value,
        isenabled: true,
        isdeleted: false,
        lastupdatedby: this.auth.currentUserValue.username,
      };
      this.store.dispatch(new sftpActions.CREATESFTP(newsftp as SftpInfo));
      form.resetForm();
    }
  }

  onDelete(row: any, services: any) {
    let dialog =
      this.services && this.services[0] ? this.services[0] : this != null ? this.dialog : null;
    let sftpService =
      this.services && this.services[1] ? this.services[1] : this != null ? this.sftpService : null;
    const dialogRef = dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: `Do you want to delete the SFTP record ${row.hostname}?` },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        row.isDeleted = true;
        sftpService.deleteSftp(row).subscribe(result => {
          let cur = result;
          sftpService.setDataChanged(true);
          sftpService.setDataChanged(false);
        });
      }
    });
  }

  onEdit(row: any, services: any) {
    let dialog =
      this.services && this.services[0] ? this.services[0] : this != null ? this.dialog : null;
    let sftpService =
      this.services && this.services[1] ? this.services[1] : this != null ? this.sftpService : null;

    const dialogRef = dialog.open(SftpEditComponent, {
      width: '550px',
      data: { row },
    });

    dialogRef.afterClosed().subscribe(result => {
      setTimeout(() => {
        let test = result;
        sftpService.setDataChanged(true);
        sftpService.setDataChanged(false);
      }, 100);
    });
  }

  GridDefaultSettings(): GridSettings {
    return {
      state: this.state,
      gridData: process(this.sftps, this.state),
      columnsConfig: [
        {
          field: 'hostname',
          filterable: true,
          hidden: false,
          title: 'Host Name',
          width: 150,
        },
        {
          field: 'sftpname',
          filterable: true,
          hidden: false,
          title: 'SFTP Name',
          width: 150,
        },
        {
          field: 'port',
          filterable: true,
          hidden: false,
          title: 'Port',
          width: 150,
        },
        {
          field: 'username',
          filterable: true,
          hidden: false,
          title: 'User Name',
          width: 150,
        },
        {
          field: 'isexternal',
          filter: 'boolean',
          filterable: true,
          hidden: false,
          title: 'Is External',
          width: 80,
        },
        {
          clickAction: this.onEdit,
          field: 'id',
          filterable: false,
          hidden: false,
          icon: 'pen',
          includeInChooser: false,
          title: 'Edit',
          type: 'icon',
          services: [this.dialog, this.sftpService],
          width: 40,
        },
        {
          clickAction: this.onDelete,
          field: 'id',
          filterable: false,
          hidden: false,
          icon: 'trash-alt',
          includeInChooser: false,
          title: 'Delete',
          type: 'icon',
          services: [this.dialog, this.sftpService],
          width: 40,
        },
      ],
    };
  }
}
